import Head from 'next/head';
import { useRouter } from 'next/router';

import { usePathUtils } from '@/hooks';
import { GeneralPath, breadcrumbsLabels } from '@/utils/models';

interface BreadcrumbSchemaProps {
  hideOnHome?: boolean;
}

export const BreadcrumbSchema = ({ hideOnHome = true }: BreadcrumbSchemaProps) => {
  const router = useRouter();
  const { pathSegments, isArticlePage, isHomePage, isServicePage, parseSegment } = usePathUtils();

  // Don't show breadcrumb schema on home page if hideOnHome is true
  if (hideOnHome && isHomePage) {
    return null;
  }

  const baseUrl = process.env.NEXT_PUBLIC_WEBSITE_URL || 'https://startup-house.com';

  const paths = [
    {
      url: '/',
      label: 'Homepage',
    },
    ...pathSegments
      .map((segment, index) => {
        if (isServicePage && segment === 'pages') {
          return null;
        }

        const urlSegments = pathSegments.slice(0, index + 1);
        let url: string;

        if (isArticlePage) {
          // For article pages, if it's the first segment (blog), use /blog
          // If it's the second segment (article slug), use the full current path
          url = index === 0 ? GeneralPath.BLOG : router.asPath;
        } else {
          url = `/${urlSegments.join('/')}`;
        }

        return {
          url,
          label: breadcrumbsLabels[segment] || parseSegment(segment),
        };
      })
      .filter((path): path is { url: string; label: string } => path !== null),
  ];

  // Only generate schema if there are multiple breadcrumb items
  if (paths.length <= 1) {
    return null;
  }

  const breadcrumbListSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: paths.map((path, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: path.label,
      item: `${baseUrl}${path.url}`,
    })),
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbListSchema),
        }}
      />
    </Head>
  );
};
